<template>
  <div class="team-slider-mobile">
    <div ref="sliderContainer" class="team-slider-mobile-container">
      <div
        v-for="member in teamMembers"
        :key="member.id"
        class="team-slider-mobile-box"
      >
        <div class="team-slider-mobile-content">
          <h3>{{ member.name }}</h3>
          <p class="team-slider-mobile-position">{{ member.position }}</p>
          <p class="team-slider-mobile-description">{{ member.description }}</p>
        </div>
        <div class="team-slider-mobile-spacer">
          <Transition name="image-fade" mode="out-in">
            <img
              :key="`img-${member.id}-${getCurrentImage(member)}`"
              :src="getCurrentImage(member)"
              :alt="member.name"
              class="team-slider-mobile-image"
              loading="lazy"
            >
          </Transition>
          <button
            class="team-slider-mobile-button"
            type="button"
            aria-label="Více informací"
            @click="toggleImage(member)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor">
              <path d="M352 128C352 110.3 337.7 96 320 96C302.3 96 288 110.3 288 128L288 288L128 288C110.3 288 96 302.3 96 320C96 337.7 110.3 352 128 352L288 352L288 512C288 529.7 302.3 544 320 544C337.7 544 352 529.7 352 512L352 352L512 352C529.7 352 544 337.7 544 320C544 302.3 529.7 288 512 288L352 288L352 128z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, nextTick, ref } from 'vue'
import { tns } from 'tiny-slider/src/tiny-slider'

interface TeamMember {
  id: number
  name: string
  position: string
  description: string
  image: string
  imageAlternate?: string | null
}

interface Props {
  teamMembers?: TeamMember[]
}

withDefaults(defineProps<Props>(), {
  teamMembers: () => []
})

const sliderContainer = ref<HTMLElement | null>(null)

// Track which members are showing alternate image
const showingAlternateImage = ref<Map<number, boolean>>(new Map())

/**
 * Get the current image for a given member (primary or alternate)
 */
function getCurrentImage(member: TeamMember): string {
  const isShowingAlternate = showingAlternateImage.value.get(member.id) ?? false
  if (isShowingAlternate && member.imageAlternate) {
    return member.imageAlternate
  }
  return member.image
}

/**
 * Toggle between primary and alternate image for a member
 */
function toggleImage(member: TeamMember): void {
  if (!member.imageAlternate) return

  const currentState = showingAlternateImage.value.get(member.id) ?? false
  showingAlternateImage.value.set(member.id, !currentState)
}

onMounted(() => {
  nextTick(() => {
    if (!sliderContainer.value) return

    const slides = sliderContainer.value.children
    if (slides.length === 0) return

    tns({
      container: sliderContainer.value,
      items: 1,
      slideBy: 1,
      gutter: 0,
      autoplay: false,
      controlsPosition: 'bottom',
      controlsText: [
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 57 57"><path fill="#fff" d="M36.599 39.377 25.72 28.5 36.6 17.599l-3.35-3.349L19 28.5l14.25 14.25 3.349-3.373z"/></svg>',
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 57 57"><path fill="#fff" d="M20.401 39.377 31.28 28.5 20.4 17.599l3.349-3.349L38 28.5 23.75 42.75l-3.349-3.373z"/></svg>'
      ],
      nav: false,
      controls: true
    } as unknown as import('tiny-slider/src/tiny-slider').TinySliderSettings)
  })
})
</script>
